import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Card, Row, Col, Select, Button, Table, Checkbox, message, Space, Modal, Radio, Form, Divider, DatePicker, Input, Tag, Statistic } from 'antd';
import { PrinterFilled, PrinterOutlined, SaveOutlined, BarChartOutlined, FileTextOutlined, SearchOutlined, FilterOutlined, DollarOutlined, CheckCircleOutlined, ClockCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import { MakeupService } from '../../services/makeupService';

dayjs.extend(quarterOfYear);

const { RangePicker } = DatePicker;

const apiBase = '/api/v1';

// Chuyển số tiền thành chữ tiếng Việt (hỗ trợ tới hàng tỷ trở lên)
function numberToVietnameseWords(num: number): string {
  const digits = ['không', 'một', 'hai', 'ba', 'bốn', 'năm', 'sáu', 'bảy', 'tám', 'chín'];
  const units = ['', ' nghìn', ' triệu', ' tỷ', ' nghìn tỷ', ' triệu tỷ'];

  function readTriple(n: number, forceHundredZero: boolean): string {
    const hundred = Math.floor(n / 100);
    const ten = Math.floor((n % 100) / 10);
    const unit = n % 10;
    let result = '';

    if (hundred > 0 || forceHundredZero) {
      result += (hundred > 0 ? digits[hundred] : 'không') + ' trăm';
      if (ten === 0 && unit > 0) {
        result += ' linh';
      }
    }

    if (ten > 1) {
      result += ' ' + digits[ten] + ' mươi';
      if (unit === 1) {
        result += ' mốt';
      } else if (unit === 5) {
        result += ' lăm';
      } else if (unit > 0) {
        result += ' ' + digits[unit];
      }
    } else if (ten === 1) {
      result += ' mười';
      if (unit === 1) {
        result += ' một';
      } else if (unit === 5) {
        result += ' lăm';
      } else if (unit > 0) {
        result += ' ' + digits[unit];
      }
    } else if (ten === 0 && unit > 0 && !forceHundredZero && hundred === 0) {
      // Trường hợp không có 'trăm' đứng trước (không thuộc rule bắt buộc)
      result += ' ' + digits[unit];
    } else if (ten === 0 && unit > 0 && (forceHundredZero || hundred > 0)) {
      // đã thêm 'linh' ở trên nếu cần, chỉ thêm đơn vị
      result += ' ' + digits[unit];
    }

    return result.trim();
  }

  if (!num || num === 0) {
    return 'Không đồng';
  }

  let n = Math.floor(Math.abs(num));
  const parts: string[] = [];
  let unitIndex = 0;

  while (n > 0) {
    const group = n % 1000;
    if (group !== 0) {
      // Rule: riêng hàng nghìn nếu thiếu hàng trăm (ví dụ 015 nghìn) thì bắt buộc đọc "không trăm"
      const forceHundredZero = (unitIndex === 1) && group < 100;
      const groupText = readTriple(group, forceHundredZero);
      parts.unshift(groupText + units[unitIndex]);
    }
    n = Math.floor(n / 1000);
    unitIndex++;
  }

  const text = parts.join(' ').replace(/\s+/g, ' ').trim();
  const capitalized = text.charAt(0).toUpperCase() + text.slice(1);
  return capitalized + ' đồng';
}

const AccountingMakeupManagementPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [courses, setCourses] = useState<any[]>([]);
  const [subjects, setSubjects] = useState<any[]>([]);
  const [classSections, setClassSections] = useState<any[]>([]);
  const [selectedCourse, setSelectedCourse] = useState<string>();
  const [selectedSubject, setSelectedSubject] = useState<string>();
  const [selectedClassSection, setSelectedClassSection] = useState<string>();
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [makeups, setMakeups] = useState<any[]>([]);
  const [changedRows, setChangedRows] = useState<Record<string, any>>({});
  const [searchText, setSearchText] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  
  // State cho chức năng thống kê bảng kê
  const [statisticsModalVisible, setStatisticsModalVisible] = useState(false);
  const [statisticsType, setStatisticsType] = useState('day');
  const [statisticsLoading, setStatisticsLoading] = useState(false);
  const [statisticsData, setStatisticsData] = useState<any[]>([]);
  const [statisticsDateRange, setStatisticsDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([dayjs().startOf('month'), dayjs()]);
  const [statisticsMonth, setStatisticsMonth] = useState(dayjs().month());
  const [statisticsQuarter, setStatisticsQuarter] = useState(dayjs().quarter());
  const [statisticsYear, setStatisticsYear] = useState(dayjs().year());

  // Lấy danh sách khóa học
  useEffect(() => {
    fetch(`${apiBase}/courses`).then(r => r.json()).then(data => {
      if (data.success) setCourses(data.data || []);
    });
  }, []);

  // Lấy danh sách môn học theo khóa học
  useEffect(() => {
    if (selectedCourse) {
      fetch(`${apiBase}/subjects/by-khoahoc/${selectedCourse}`)
        .then(r => r.json())
        .then(data => {
          if (data.success) setSubjects(data.data || []);
        });
      setSelectedSubject(undefined);
      setSelectedClassSection(undefined);
      setClassSections([]);
    } else {
      setSubjects([]);
      setSelectedSubject(undefined);
      setSelectedClassSection(undefined);
      setClassSections([]);
    }
  }, [selectedCourse]);

  // Lấy danh sách lớp học phần theo khóa học và môn học
  useEffect(() => {
    if (selectedSubject) {
      setLoading(true);
      
      fetch(`${apiBase}/lophocphan/by-monhoc/${selectedSubject}`)
        .then(r => r.json())
        .then(data => {
          let sections = [];
          
          if (data.success && Array.isArray(data.data)) {
            sections = data.data;
          } else if (Array.isArray(data)) {
            sections = data;
          }
          
          // Áp dụng lọc theo khóa học nếu cần
          if (selectedCourse) {
            sections = sections.filter((lhp: any) => {
              const lhpKhoaHoc = String(lhp.KhoaHocID || lhp.khoaHocId || '');
              return !lhpKhoaHoc || lhpKhoaHoc === String(selectedCourse);
            });
          }
          
          setClassSections(sections);
          setLoading(false);
        })
        .catch(() => {
          setClassSections([]);
          setLoading(false);
        });
        
      setSelectedClassSection(undefined);
    } else {
      setClassSections([]);
      setSelectedClassSection(undefined);
    }
  }, [selectedCourse, selectedSubject]);

  // Lấy danh sách sinh viên học bù theo filter
  useEffect(() => {
    fetchMakeups();
  }, [selectedCourse, selectedSubject, selectedClassSection, selectedDate]);

  // Lấy danh sách học bù
  const fetchMakeups = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (selectedCourse) params.append('course', selectedCourse);
    if (selectedSubject) params.append('subject', selectedSubject);
    if (selectedClassSection) params.append('classSection', selectedClassSection);
    if (selectedDate) params.append('date', selectedDate.format('YYYY-MM-DD'));
    
    fetch(`${apiBase}/hocbu?${params.toString()}`)
      .then(r => r.json())
      .then(data => {
        if (data.success) {
          setMakeups(data.data || []);
        } else {
          setMakeups([]);
        }
      })
      .catch(() => setMakeups([]))
      .finally(() => setLoading(false));
  }, [selectedCourse, selectedSubject, selectedClassSection, selectedDate]);

  function handleCashChange(checked: boolean, record: any) {
    setChangedRows(rows => {
      const prev = rows[record.HocBuID] || record;
      const updatedTienMat = checked;
      const updatedChuyenKhoan = prev.ChuyenKhoan ?? !!record.ChuyenKhoan;
      const isPaid = updatedTienMat || updatedChuyenKhoan;
      return {
        ...rows,
        [record.HocBuID]: {
          ...prev,
          TienMat: updatedTienMat,
          ChuyenKhoan: updatedChuyenKhoan,
          DaDongTien: isPaid,
          TrangThai: isPaid ? 'Đã đóng tiền' : 'Chưa đóng tiền'
        }
      };
    });
  }

  function handleBankChange(checked: boolean, record: any) {
    setChangedRows(rows => {
      const prev = rows[record.HocBuID] || record;
      const updatedChuyenKhoan = checked;
      const updatedTienMat = prev.TienMat ?? !!record.TienMat;
      const isPaid = updatedTienMat || updatedChuyenKhoan;
      return {
        ...rows,
        [record.HocBuID]: {
          ...prev,
          TienMat: updatedTienMat,
          ChuyenKhoan: updatedChuyenKhoan,
          DaDongTien: isPaid,
          TrangThai: isPaid ? 'Đã đóng tiền' : 'Chưa đóng tiền'
        }
      };
    });
  }

  async function handleSave() {
    const updates = Object.values(changedRows);
    if (updates.length === 0) {
      message.info('Không có thay đổi để lưu.');
      return;
    }
    setLoading(true);
    try {
      console.log('🔄 Saving payment status changes:', updates);
      
      const results = await Promise.all(Object.values(changedRows).map(async (row: any) => {
        const url = `${apiBase}/hocbu/accounting/${row.HocBuID}`;
        const payload = { 
          DaDongTien: row.DaDongTien, 
          TienMat: (() => { const orig = makeups.find(m => m.HocBuID === row.HocBuID) || {}; return (row.TienMat ?? (orig as any).TienMat ?? false); })(),
          ChuyenKhoan: (() => { const orig = makeups.find(m => m.HocBuID === row.HocBuID) || {}; return (row.ChuyenKhoan ?? (orig as any).ChuyenKhoan ?? false); })(),
          TrangThai: row.TrangThai 
        };
        
        console.log(`📡 PUT ${url}`, payload);
        
        const response = await fetch(url, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        
        const result = await response.json();
        console.log(`✅ Response for ${row.HocBuID}:`, result);
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${result.message || 'Unknown error'}`);
        }
        
        return result;
      }));
      
      console.log('✅ All updates completed:', results);
      message.success(`Đã lưu ${updates.length} thay đổi trạng thái đóng tiền!`);
      setChangedRows({});
      await fetchMakeups(); // Reload data to reflect changes
    } catch (error) {
      console.error('❌ Error saving payment status:', error);
      message.error(`Lỗi khi lưu trạng thái: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  }

  function handlePrintReceipt(record: any) {
    // Tìm tất cả các buổi học bù của cùng sinh viên và môn học
    const studentMakeups = makeups.filter(makeup => 
      makeup.MaHS === record.MaHS && 
      makeup.MonHocID === record.MonHocID
    );
    
    // Hiển thị thông báo số buổi sẽ được in
    if (studentMakeups.length > 1) {
      message.info(`Sẽ in phiếu thu gộp ${studentMakeups.length} buổi của sinh viên ${record.HoTen}`);
    }
    
    // Tính tổng tiền từ tất cả các buổi
    const soTien = studentMakeups.reduce((total, makeup) => total + (makeup.ThanhTien || 0), 0);
    const lyDo = `Tiền ${record.HinhThuc || 'Học bù'} môn ${record.TenMonHoc || 'Tin học'} (${studentMakeups.length} buổi)`;
    const soTienBangChu = numberToVietnameseWords(soTien);
    const currentDate = selectedDate?.format('DD/MM/YYYY') || dayjs().format('DD/MM/YYYY');
    
    // Create print window
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    
    // Function to create one receipt page (full A5 page)
    const createReceiptPage = () => `
      <div class="receipt-page">
        <!-- Header với 3 cột -->
        <table style="width: 100%; border-collapse: collapse; margin-bottom: 10mm;">
          <tr>
            <td style="width: 35%; vertical-align: top; padding: 0;">
              <div style="font-size: 12px; line-height: 1.2; font-weight: bold;">
                TRƯỜNG CAO ĐẲNG Y TẾ THÁI BÌNH<br>
                <span style="font-weight: normal;">Mã đơn vị SDNS: 1021623</span>
              </div>
            </td>
            <td style="width: 35%; text-align: center; vertical-align: top; padding: 0;">
              <div style="font-size: 20px; font-weight: bold; margin: 3mm 0;">PHIẾU THU</div>
              <div style="font-size: 12px;">Ngày ${currentDate}</div>
            </td>
            <td style="width: 30%; text-align: right; vertical-align: top; padding: 0; font-size: 10px; line-height: 1.1;">
              Mẫu số C30 BB<br>
              (Ban hành theo QĐ số 19/2006/QĐ-BTC<br>
              Ngày 30/3/2006 của Bộ trưởng Bộ TC)<br>
              Số:...............<br>
              Nợ:...............<br>
              Có:...............
            </td>
          </tr>
        </table>
        
        <!-- Nội dung chính -->
        <div style="margin: 8mm 0; font-size: 16px; line-height: 1.5;">
          <div style="margin: 3mm 0; font-size: 16px;">Họ và tên học sinh, sinh viên: <strong>${record.HoTen || 'Hoàng Thị Thảo'}</strong></div>
          <div style="margin: 3mm 0; font-size: 16px;">Lớp: <strong>${record.TenLop || record.LopSinhHoat || 'DD-K17A'}</strong></div>
          <div style="margin: 3mm 0; font-size: 16px;">Lý do nộp: <em>${lyDo}</em></div>
          <div style="margin: 3mm 0; font-size: 16px;">Số tiền: <strong>${soTien.toLocaleString('vi-VN')}</strong> (Viết bằng chữ): <em>${soTienBangChu}</em></div>
          
          <!-- Tổng kết các buổi học bù -->
          <div style="margin: 3mm 0; font-size: 16px;">
            <strong>Tổng kết:</strong> ${studentMakeups.length} buổi - 
            ${studentMakeups.reduce((total, makeup) => total + (makeup.SoTietLT || 0), 0)} tiết LT, 
            ${studentMakeups.reduce((total, makeup) => total + (makeup.SoTietTH || 0), 0)} tiết TH
            ${studentMakeups.some(makeup => makeup.CoHoaChat) ? ' (Có hóa chất)' : ''}
          </div>
          
          <div style="margin: 3mm 0;">Kèm theo: ....... chứng từ gốc</div>
        </div>
        
        <!-- Chữ ký ở cuối trang -->
        <div style="position: absolute; bottom: 30mm; left: 15mm; right: 15mm;">
          <table style="width: 100%; font-size: 16px;">
            <tr>
              <td style="width: 33%; text-align: center; font-size: 16px; vertical-align: top;">
                <strong>Thủ trưởng đơn vị</strong><br>
                <span style="font-style: italic;">(Ký, họ tên, đóng dấu)</span>
              </td>
              <td style="width: 33%; text-align: center; font-size: 16px; vertical-align: top;">
                <strong>Phòng Kế toán</strong><br>
                <span style="font-style: italic;">(Ký, họ tên)</span>
              </td>
              <td style="width: 33%; text-align: center; font-size: 16px; vertical-align: top;">
                <strong>Người thu tiền</strong><br>
                <span style="font-style: italic;">(Ký, họ tên)</span>
              </td>
            </tr>
          </table>
        </div>
      </div>
    `;
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>PHIẾU THU</title>
        <meta charset="utf-8">
        <style>
          @page {
            size: 210mm 148mm;
            margin: 10mm 15mm;
          }
          
          * {
            box-sizing: border-box;
          }
          
          body { 
            font-family: 'Times New Roman', Times, serif; 
            margin: 0;
            padding: 0;
            font-size: 16px;
            line-height: 1.2;
            width: 210mm;
            height: 148mm;
          }
          
          .receipt-page {
            width: 210mm;
            height: 148mm;
            page-break-after: always;
            padding: 10mm 15mm;
            position: relative;
            overflow: hidden;
          }
          
          .receipt-page:last-child {
            page-break-after: auto;
          }
          
          @media screen {
            body {
              background: #f0f0f0;
              padding: 20px;
            }
            .receipt-page {
              background: white;
              box-shadow: 0 0 10px rgba(0,0,0,0.1);
              margin-bottom: 20px;
            }
          }
          
          @media print {
            @page {
              size: 210mm 148mm;
              margin: 0;
            }
            
            body { 
              margin: 0;
              padding: 0;
              background: white;
            }
            
            .receipt-page { 
              width: 210mm;
              height: 148mm;
              margin: 0;
              padding: 10mm 15mm;
              box-shadow: none;
              page-break-inside: avoid;
            }
          }
        </style>
      </head>
      <body>
        ${createReceiptPage()}
        ${createReceiptPage()}
      </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.focus();
    
    // Auto print after a short delay to ensure content is loaded
    setTimeout(() => {
      printWindow.print();
    }, 500);
  }

  // In tất cả phiếu thu theo cách gộp
  function handlePrintAllReceipts() {
    if (makeups.length === 0) {
      message.warning('Không có dữ liệu học bù để in');
      return;
    }

    // Nhóm các bản ghi theo sinh viên và môn học
    const groupedMakeups: { [key: string]: any[] } = {};
    makeups.forEach(makeup => {
      const key = `${makeup.MaHS}_${makeup.MonHocID}`;
      if (!groupedMakeups[key]) {
        groupedMakeups[key] = [];
      }
      groupedMakeups[key].push(makeup);
    });

    const studentGroups = Object.values(groupedMakeups);
    message.info(`Sẽ in ${studentGroups.length} phiếu thu gộp từ ${makeups.length} bản ghi`);

    // Tạo nội dung HTML cho tất cả phiếu thu
    const allReceiptsContent = studentGroups.map((studentMakeups, index) => {
      const record = studentMakeups[0]; // Lấy thông tin sinh viên từ bản ghi đầu tiên
      const soTien = studentMakeups.reduce((total, makeup) => total + (makeup.ThanhTien || 0), 0);
      const lyDo = `Tiền ${record.HinhThuc || 'Học bù'} môn ${record.TenMonHoc || 'Tin học'} (${studentMakeups.length} buổi)`;
      const soTienBangChu = numberToVietnameseWords(soTien);
      const currentDate = selectedDate?.format('DD/MM/YYYY') || dayjs().format('DD/MM/YYYY');

      return `
        <div class="receipt-page">
          <!-- Header với 3 cột -->
          <table style="width: 100%; border-collapse: collapse; margin-bottom: 10mm;">
            <tr>
              <td style="width: 35%; vertical-align: top; padding: 0;">
                <div style="font-size: 13px; line-height: 1.2; font-weight: bold;">
                  TRƯỜNG CAO ĐẲNG Y TẾ THÁI BÌNH<br>
                  <span style="font-weight: normal;">Mã đơn vị SDNS: 1021623</span>
                </div>
              </td>
              <td style="width: 35%; text-align: center; vertical-align: top; padding: 0;">
                <div style="font-size: 20px; font-weight: bold; margin: 3mm 0;">PHIẾU THU</div>
                <div style="font-size: 13px;">Ngày ${currentDate}</div>
              </td>
              <td style="width: 30%; text-align: right; vertical-align: top; padding: 0; font-size: 10px; line-height: 1.1;">
                Mẫu số C30 BB<br>
                (Ban hành theo QĐ số 19/2006/QĐ-BTC<br>
                Ngày 30/3/2006 của Bộ trưởng Bộ TC)<br>
                Số:...............<br>
                Nợ:...............<br>
                Có:...............
              </td>
            </tr>
          </table>
          
          <!-- Nội dung chính -->
          <div style="margin: 8mm 0; font-size: 16px; line-height: 1.5;">
            <div style="margin: 3mm 0; font-size: 16px;">Họ và tên học sinh, sinh viên: <strong>${record.HoTen || 'Hoàng Thị Thảo'}</strong></div>
            <div style="margin: 3mm 0; font-size: 16px;">Lớp: <strong>${record.TenLop || record.LopSinhHoat || 'DD-K17A'}</strong></div>
            <div style="margin: 3mm 0; font-size: 16px;">Lý do nộp: <em>${lyDo}</em></div>
            <div style="margin: 3mm 0; font-size: 16px;">Số tiền: <strong>${soTien.toLocaleString('vi-VN')}</strong> (Viết bằng chữ): <em>${soTienBangChu}</em></div>
            
            <!-- Tổng kết các buổi học bù -->
            <div style="margin: 3mm 0; font-size: 16px;">
              <strong>Tổng kết:</strong> ${studentMakeups.length} buổi - 
              ${studentMakeups.reduce((total, makeup) => total + (makeup.SoTietLT || 0), 0)} tiết LT, 
              ${studentMakeups.reduce((total, makeup) => total + (makeup.SoTietTH || 0), 0)} tiết TH
              ${studentMakeups.some(makeup => makeup.CoHoaChat) ? ' (Có hóa chất)' : ''}
            </div>
            
            <div style="margin: 3mm 0;">Kèm theo: ....... chứng từ gốc</div>
          </div>
          
          <!-- Chữ ký ở cuối trang -->
          <div style="position: absolute; bottom: 30mm; left: 15mm; right: 15mm;">
            <table style="width: 100%; font-size: 15px;">
              <tr>
                <td style="width: 33%; text-align: center; vertical-align: top;">
                  <strong>Thủ trưởng đơn vị</strong><br>
                  <span style="font-style: italic;">(Ký, họ tên, đóng dấu)</span>
                </td>
                <td style="width: 33%; text-align: center; vertical-align: top;">
                  <strong>Phòng Kế toán</strong><br>
                  <span style="font-style: italic;">(Ký, họ tên)</span>
                </td>
                <td style="width: 33%; text-align: center; vertical-align: top;">
                  <strong>Người thu tiền</strong><br>
                  <span style="font-style: italic;">(Ký, họ tên)</span>
                </td>
              </tr>
            </table>
          </div>
        </div>
        ${index < studentGroups.length - 1 ? '<div style="page-break-after: always;"></div>' : ''}
      `;
    }).join('');

    // Tạo cửa sổ in
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>TẤT CẢ PHIẾU THU</title>
        <meta charset="utf-8">
        <style>
          @page {
            size: 210mm 148mm;
            margin: 10mm 15mm;
          }
          
          * {
            box-sizing: border-box;
          }
          
          body { 
            font-family: 'Times New Roman', Times, serif; 
            margin: 0;
            padding: 0;
            font-size: 16px;
            line-height: 1.2;
            width: 210mm;
            height: 148mm;
          }
          
          .receipt-page {
            width: 210mm;
            height: 148mm;
            page-break-after: always;
            padding: 10mm 15mm;
            position: relative;
            overflow: hidden;
          }
          
          .receipt-page:last-child {
            page-break-after: auto;
          }
          
          @media screen {
            body {
              background: #f0f0f0;
              padding: 20px;
            }
            .receipt-page {
              background: white;
              box-shadow: 0 0 10px rgba(0,0,0,0.1);
              margin-bottom: 20px;
            }
          }
          
          @media print {
            @page {
              size: 210mm 148mm;
              margin: 0;
            }
            
            body { 
              margin: 0;
              padding: 0;
              background: white;
            }
            
            .receipt-page { 
              width: 210mm;
              height: 148mm;
              margin: 0;
              padding: 10mm 15mm;
              box-shadow: none;
              page-break-inside: avoid;
            }
          }
        </style>
      </head>
      <body>
        ${allReceiptsContent}
      </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();

    // Auto print after a short delay
    setTimeout(() => {
      printWindow.print();
    }, 500);
  }

  // In giấy xác nhận thanh toán chuyển khoản
  function handlePrintBankConfirmation(record: any) {
    const studentMakeups = makeups.filter(m => m.MaHS === record.MaHS && m.MonHocID === record.MonHocID);
    const soTien = studentMakeups.reduce((t, m) => t + (m.ThanhTien || 0), 0);
    const currentDate = selectedDate?.format('DD/MM/YYYY') || dayjs().format('DD/MM/YYYY');
    const soTienBangChu = numberToVietnameseWords(soTien);

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8" />
        <title>Giấy xác nhận thanh toán chuyển khoản</title>
        <style>
          @page { size: A5 landscape; margin: 15mm; }
          body { font-family: 'Times New Roman', Times, serif; }
          .title { text-align: center; font-size: 18px; font-weight: bold; margin: 10px 0; }
          .center { text-align: center; }
          .row { margin: 8px 0; }
        </style>
      </head>
      <body>
        <table style="width:100%; border-collapse:collapse; margin-bottom: 8px;">
          <tr>
            <td style="width:50%; vertical-align: top; text-align: center;">
              <div style="font-weight:bold; font-size: 13px;">TRƯỜNG CAO ĐẲNG Y TẾ THÁI BÌNH</div>
              <div><u>Mã đơn vị SDNS: 1021623</u></div>
            </td>
            <td style="width:50%; vertical-align: top; text-align: center;">
              <div style="font-weight:bold; font-size: 13px;">CỘNG HÒA XÃ HỘI CHỦ NGHĨA VIỆT NAM</div>
              <div><u>Độc lập - Tự do - Hạnh phúc</u></div>
              <div style="margin-top:6px;">Ngày ${currentDate.split('/')[0]} tháng ${currentDate.split('/')[1]} năm ${currentDate.split('/')[2]}</div>
            </td>
          </tr>
        </table>
        <div class="title">GIẤY XÁC NHẬN THANH TOÁN CHUYỂN KHOẢN</div>
        <div class="row"><strong>Trường Cao đẳng Y tế Thái Bình xác nhận:</strong></div>
        <div class="row">Họ và tên sinh viên: <strong>${record.HoTen || ''}</strong></div>
        <div class="row">Lớp: <strong>${record.TenLop || record.LopSinhHoat || ''}</strong></div>
        <div class="row">Lý do nộp: Tiền ${record.HinhThuc || 'Học bù'} môn ${record.TenMonHoc || ''}</div>
        <div class="row">Số tiền: <strong>${soTien.toLocaleString('vi-VN')}</strong> (Viết bằng chữ): <em>${soTienBangChu}</em></div>
        <div class="row">Tổng kết: Sinh viên đã nộp đủ số tiền trên bằng hình thức chuyển khoản vào tài khoản ngân hàng của nhà trường.</div>
        <div class="row">Giấy xác nhận này được đính kèm với phiếu học bù, học lại.</div>
        <div style="text-align:right; margin-top:30px; font-weight:bold;">Phòng Kế hoạch Tài chính</div>
      </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 400);
  }

  // Mở modal thống kê bảng kê
  function openStatisticsModal() {
    setStatisticsModalVisible(true);
  }

  // Đóng modal thống kê bảng kê
  function closeStatisticsModal() {
    setStatisticsModalVisible(false);
  }

  // Xử lý thay đổi loại thống kê
  function handleStatisticsTypeChange(e: any) {
    setStatisticsType(e.target.value);
  }

  // Lấy dữ liệu thống kê bảng kê (chỉ lấy những bản ghi đã đóng tiền)
  async function fetchStatisticsData() {
    setStatisticsLoading(true);
    try {
      let startDate, endDate;
      
      switch (statisticsType) {
        case 'day':
          if (statisticsDateRange && statisticsDateRange[0] && statisticsDateRange[1]) {
            startDate = statisticsDateRange[0].format('YYYY-MM-DD');
            endDate = statisticsDateRange[1].format('YYYY-MM-DD');
          } else {
            startDate = dayjs().startOf('month').format('YYYY-MM-DD');
            endDate = dayjs().format('YYYY-MM-DD');
          }
          break;
        case 'month':
          startDate = dayjs().year(statisticsYear).month(statisticsMonth).startOf('month').format('YYYY-MM-DD');
          endDate = dayjs().year(statisticsYear).month(statisticsMonth).endOf('month').format('YYYY-MM-DD');
          break;
        case 'quarter':
          startDate = dayjs().year(statisticsYear).quarter(statisticsQuarter).startOf('quarter').format('YYYY-MM-DD');
          endDate = dayjs().year(statisticsYear).quarter(statisticsQuarter).endOf('quarter').format('YYYY-MM-DD');
          break;
        case 'year':
          startDate = dayjs().year(statisticsYear).startOf('year').format('YYYY-MM-DD');
          endDate = dayjs().year(statisticsYear).endOf('year').format('YYYY-MM-DD');
          break;
        default:
          startDate = dayjs().startOf('month').format('YYYY-MM-DD');
          endDate = dayjs().format('YYYY-MM-DD');
      }

      // Gọi API để lấy dữ liệu thống kê - chỉ lấy những bản ghi đã đóng tiền (DaDongTien=1)
      const url = `${apiBase}/hocbu/statistics?startDate=${startDate}&endDate=${endDate}&daDongTien=true`;
      const response = await fetch(url);
      const data = await response.json();
      
      console.log('API statistics response:', data);
      
      // Xử lý response structure từ API
      let filteredData = [];
      if (data.success && data.data && data.data.details) {
        // API trả về đúng format với details array
        filteredData = data.data.details.filter((item: any) => item.DaDongTien === true || item.DaDongTien === 1);
      } else if (Array.isArray(data)) {
        // Fallback cho format cũ
        filteredData = data.filter((item: any) => item.DaDongTien === true || item.DaDongTien === 1);
      } else {
        console.warn('Unexpected API response format:', data);
        filteredData = [];
      }
      
      setStatisticsData(filteredData);
      
      if (filteredData.length === 0) {
        message.info('Không có dữ liệu đã đóng tiền trong khoảng thời gian này!');
      } else {
        message.success(`Đã lấy ${filteredData.length} bản ghi đã đóng tiền!`);
      }
    } catch (error) {
      console.error('Lỗi khi lấy dữ liệu thống kê:', error);
      message.error('Lỗi khi lấy dữ liệu thống kê!');
    } finally {
      setStatisticsLoading(false);
    }
  }

  // In bảng kê nộp tiền
  function printStatisticsReport() {
    if (!statisticsData.length) {
      message.warning('Không có dữ liệu để in bảng kê!');
      return;
    }

    // Tính tổng tiền
    const totalAmount = statisticsData.reduce((sum, item) => sum + (item.ThanhTien || 0), 0);
    const totalCash = statisticsData.reduce((sum, item) => sum + ((item.TienMat ? (item.ThanhTien || 0) : 0)), 0);
    const totalBank = statisticsData.reduce((sum, item) => sum + ((item.ChuyenKhoan ? (item.ThanhTien || 0) : 0)), 0);
    
    // Chuẩn bị dữ liệu để in
    let title = 'BẢNG KÊ NỘP TIỀN HỌC BÙ, HỌC LẠI';
    let period = '';
    
    switch (statisticsType) {
      case 'day':
        if (statisticsDateRange && statisticsDateRange[0] && statisticsDateRange[1]) {
          period = `Từ ngày ${statisticsDateRange[0].format('DD/MM/YYYY')} đến ngày ${statisticsDateRange[1].format('DD/MM/YYYY')}`;
        } else {
          period = `Từ ngày ${dayjs().startOf('month').format('DD/MM/YYYY')} đến ngày ${dayjs().format('DD/MM/YYYY')}`;
        }
        break;
      case 'month':
        period = `Tháng ${statisticsMonth + 1}/${statisticsYear}`;
        break;
      case 'quarter':
        period = `Quý ${statisticsQuarter}/${statisticsYear}`;
        break;
      case 'year':
        period = `Năm ${statisticsYear}`;
        break;
    }

    // Tạo cửa sổ in mới
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>${title}</title>
        <style>
          body { font-family: 'Times New Roman', Times, serif; margin: 20px; font-size: 12px; }
          .header { text-align: center; margin-bottom: 20px; }
          .title { font-size: 18px; font-weight: bold; text-transform: uppercase; margin: 10px 0; }
          table { width: 100%; border-collapse: collapse; }
          th, td { border: 1px solid #000; padding: 5px; text-align: left; }
          th { background-color: #f2f2f2; }
          .amount { text-align: right; }
          .total-row { font-weight: bold; }
          .summary { margin: 15px 0; }
          .signatures { display: flex; justify-content: space-between; margin-top: 20px; }
          .signature { text-align: center; width: 30%; }
        </style>
      </head>
      <body>
        <div class="header">
          <div style="text-align: left;">TRƯỜNG CAO ĐẲNG Y TẾ THÁI BÌNH</div>
          <div style="text-align: left;">SDNS: 1021623</div>
          <div class="title">${title}</div>
          <div>${period}</div>
        </div>
        
        <table>
          <thead>
            <tr>
              <th style="width: 30px; text-align: center;">STT</th>
              <th style="width: 200px;">Họ và tên</th>
              <th style="width: 100px; text-align: center;">Ngày sinh</th>
              <th style="width: 100px; text-align: center;">Lớp</th>
              <th style="width: 150px;">Môn học</th>
              <th style="width: 100px;">Hình thức</th>
              <th style="width: 110px; text-align: center;">PTTT</th>
              <th style="width: 110px; text-align: right;">Tiền mặt</th>
              <th style="width: 130px; text-align: right;">Chuyển khoản</th>
              <th style="width: 100px;">Ghi chú</th>
            </tr>
          </thead>
          <tbody>
            ${statisticsData.map((item, index) => `
              <tr>
                <td style="text-align: center;">${index + 1}</td>
                <td>${item.HoTen || ''}</td>
                <td style="text-align: center;">${item.NgaySinh ? dayjs(item.NgaySinh).format('DD/MM/YYYY') : ''}</td>
                <td style="text-align: center;">${item.TenLop || item.LopSinhHoat || ''}</td>
                <td>${item.TenMonHoc || ''}</td>
                <td>${item.HinhThuc || ''}</td>
                <td style="text-align: center;">${item.TienMat ? 'Tiền mặt' : (item.ChuyenKhoan ? 'Chuyển khoản' : '')}</td>
                <td style="text-align: right;">${(item.TienMat ? (item.ThanhTien || 0) : 0).toLocaleString('vi-VN')}</td>
                <td style="text-align: right;">${(item.ChuyenKhoan ? (item.ThanhTien || 0) : 0).toLocaleString('vi-VN')}</td>
                <td></td>
              </tr>
            `).join('')}
            <tr class="total-row">
              <td colspan="7" style="text-align: center;">Tổng cộng:</td>
              <td style="text-align: right;">${totalCash.toLocaleString('vi-VN')}</td>
              <td style="text-align: right;">${totalBank.toLocaleString('vi-VN')}</td>
              <td></td>
            </tr>
          </tbody>
        </table>
        
        <div class="summary">
          <p><strong>Tổng cộng tiền mặt:</strong> ${totalCash.toLocaleString('vi-VN')} đ</p>
          <p><strong>Tổng cộng tiền chuyển khoản:</strong> ${totalBank.toLocaleString('vi-VN')} đ</p>
          <p><strong>Tổng cộng:</strong> ${(totalCash + totalBank).toLocaleString('vi-VN')} đ</p>
          <p>Số tiền bằng chữ: ${numberToVietnameseWords(totalCash + totalBank)}</p>
        </div>
        
        <div style="text-align: right; margin-top: 20px;">
          Thái Bình, ngày ${new Date().getDate()} tháng ${new Date().getMonth() + 1} năm ${new Date().getFullYear()}
        </div>
        
        <div class="signatures">
          <div class="signature">
            <p>Thủ trưởng đơn vị</p>
            <p><em>(Ký, họ tên đóng dấu)</em></p>
          </div>
          
          <div class="signature">
            <p>Phòng Kế toán</p>
            <p><em>(Ký, họ tên)</em></p>
          </div>
          
          <div class="signature">
            <p>Người thu tiền</p>
            <p><em>(Ký, họ tên)</em></p>
          </div>
        </div>
      </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.focus();
    message.success('Mở bảng kê để xem trước và in!', 3);
  }

  // Lọc dữ liệu theo tìm kiếm và trạng thái
  const filteredData = useMemo(() => {
    let filtered = [...makeups];
    
    // Lọc theo văn bản tìm kiếm
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter(item => 
        (item.MaHS && item.MaHS.toLowerCase().includes(searchLower)) ||
        (item.HoTen && item.HoTen.toLowerCase().includes(searchLower)) ||
        (item.TenLop && item.TenLop.toLowerCase().includes(searchLower)) ||
        (item.TenMonHoc && item.TenMonHoc.toLowerCase().includes(searchLower))
      );
    }
    
    // Lọc theo trạng thái đóng tiền
    if (filterStatus !== 'all') {
      const isDaPaid = filterStatus === 'paid';
      filtered = filtered.filter(item => {
        const isPaid = item.DaDongTien || (changedRows[item.HocBuID]?.DaDongTien ?? false);
        return isDaPaid ? isPaid : !isPaid;
      });
    }
    
    // Sắp xếp để người chưa đóng tiền lên trên cùng
    return filtered.sort((a, b) => {
      const isPaidA = a.DaDongTien || (changedRows[a.HocBuID]?.DaDongTien ?? false);
      const isPaidB = b.DaDongTien || (changedRows[b.HocBuID]?.DaDongTien ?? false);
      
      if (isPaidA === isPaidB) {
        // Nếu cùng trạng thái, sắp xếp theo tên
        return (a.HoTen || '').localeCompare(b.HoTen || '');
      }
      
      // Chưa đóng tiền lên trên
      return isPaidA ? 1 : -1;
    });
  }, [makeups, searchText, filterStatus, changedRows]);

  // Tính toán thống kê hiện tại
  const currentStats = filteredData.reduce((acc, record) => {
    acc.total = filteredData.length;
    const row = changedRows[record.HocBuID] || record;
    const isPaid = row.DaDongTien || row.TienMat || row.ChuyenKhoan || false;
    acc.paid += isPaid ? 1 : 0;
    acc.unpaid += isPaid ? 0 : 1;
    acc.totalAmount += record.ThanhTien || 0;
    const isCash = row.TienMat ? true : false;
    const isBank = row.ChuyenKhoan ? true : false;
    if (isCash) acc.paidCashAmount += (record.ThanhTien || 0);
    if (isBank) acc.paidBankAmount += (record.ThanhTien || 0);
    acc.paidAmount += isPaid ? (record.ThanhTien || 0) : 0;
    acc.unpaidAmount += isPaid ? 0 : (record.ThanhTien || 0);
    return acc;
  }, { total: 0, paid: 0, unpaid: 0, totalAmount: 0, paidAmount: 0, unpaidAmount: 0, paidCashAmount: 0, paidBankAmount: 0 });

  const columns = [
    { 
      title: 'Mã SV', 
      dataIndex: 'MaHS', 
      key: 'MaHS', 
      width: 100,
      sorter: (a: any, b: any) => (a.MaHS || '').localeCompare(b.MaHS || '')
    },
    { 
      title: 'Họ và tên', 
      dataIndex: 'HoTen', 
      key: 'HoTen', 
      width: 150,
      sorter: (a: any, b: any) => (a.HoTen || '').localeCompare(b.HoTen || '')
    },
    { 
      title: 'Ngày sinh', 
      dataIndex: 'NgaySinh', 
      key: 'NgaySinh', 
      width: 120, 
      render: (v: any) => v ? dayjs(v).format('DD/MM/YYYY') : '', 
      align: 'center' as const,
      sorter: (a: any, b: any) => {
        if (!a.NgaySinh && !b.NgaySinh) return 0;
        if (!a.NgaySinh) return -1;
        if (!b.NgaySinh) return 1;
        return dayjs(a.NgaySinh).unix() - dayjs(b.NgaySinh).unix();
      }
    },
    { 
      title: 'Lớp sinh hoạt', 
      dataIndex: 'TenLop', 
      key: 'TenLop', 
      width: 120, 
      align: 'center' as const,
      sorter: (a: any, b: any) => (a.TenLop || '').localeCompare(b.TenLop || '')
    },
    { 
      title: 'Số tiết LT', 
      dataIndex: 'SoTietLT', 
      key: 'SoTietLT', 
      width: 60, 
      align: 'center' as const,
      sorter: (a: any, b: any) => (a.SoTietLT || 0) - (b.SoTietLT || 0)
    },
    { 
      title: 'Số tiết TH', 
      dataIndex: 'SoTietTH', 
      key: 'SoTietTH', 
      width: 60, 
      align: 'center' as const,
      sorter: (a: any, b: any) => (a.SoTietTH || 0) - (b.SoTietTH || 0)
    },
    { 
      title: 'Môn học', 
      dataIndex: 'TenMonHoc', 
      key: 'TenMonHoc', 
      width: 120, 
      align: 'center' as const,
      sorter: (a: any, b: any) => (a.TenMonHoc || '').localeCompare(b.TenMonHoc || '')
    },
    { 
      title: 'Hình thức', 
      dataIndex: 'HinhThuc', 
      key: 'HinhThuc', 
      width: 90, 
      align: 'center' as const,
      sorter: (a: any, b: any) => (a.HinhThuc || '').localeCompare(b.HinhThuc || '')
    },
    { 
      title: 'Thành Tiền', 
      dataIndex: 'ThanhTien', 
      key: 'ThanhTien', 
      width: 90, 
      render: (v: any) => v ? v.toLocaleString('vi-VN') : 0, 
      align: 'right' as const,
      sorter: (a: any, b: any) => (a.ThanhTien || 0) - (b.ThanhTien || 0)
    },
    { 
      title: 'Trạng thái', 
      dataIndex: 'TrangThai', 
      key: 'TrangThai', 
      width: 120, 
      align: 'center' as const,
      render: (text: any, record: any) => {
        const status = changedRows[record.HocBuID]?.TrangThai || text;
        return status === 'Đã đóng tiền'
          ? <Tag color="success" style={{ fontWeight: 600 }}>{status}</Tag>
          : <Tag color="warning" style={{ fontWeight: 600 }}>{status || 'Chưa đóng tiền'}</Tag>;
      },
      sorter: (a: any, b: any) => {
        const statusA = changedRows[a.HocBuID]?.TrangThai || a.TrangThai || '';
        const statusB = changedRows[b.HocBuID]?.TrangThai || b.TrangThai || '';
        return statusA.localeCompare(statusB);
      }
    },
    {
      title: 'Tiền mặt',
      dataIndex: 'TienMat',
      key: 'TienMat',
      width: 100,
      align: 'center' as const,
      render: (v: any, record: any) => (
        <Checkbox
          checked={changedRows[record.HocBuID]?.TienMat ?? !!record.TienMat}
          onChange={e => handleCashChange(e.target.checked, record)}
        />
      ),
      sorter: (a: any, b: any) => {
        const aVal = changedRows[a.HocBuID]?.TienMat ?? !!a.TienMat;
        const bVal = changedRows[b.HocBuID]?.TienMat ?? !!b.TienMat;
        return aVal === bVal ? 0 : (aVal ? 1 : -1);
      }
    },
    {
      title: 'Chuyển khoản',
      dataIndex: 'ChuyenKhoan',
      key: 'ChuyenKhoan',
      width: 120,
      align: 'center' as const,
      render: (v: any, record: any) => (
        <Checkbox
          checked={changedRows[record.HocBuID]?.ChuyenKhoan ?? !!record.ChuyenKhoan}
          onChange={e => handleBankChange(e.target.checked, record)}
        />
      ),
      sorter: (a: any, b: any) => {
        const aVal = changedRows[a.HocBuID]?.ChuyenKhoan ?? !!a.ChuyenKhoan;
        const bVal = changedRows[b.HocBuID]?.ChuyenKhoan ?? !!b.ChuyenKhoan;
        return aVal === bVal ? 0 : (aVal ? 1 : -1);
      }
    },
    {
      title: 'In phiếu thu',
      key: 'printReceipt',
      width: 110,
      align: 'center' as const,
      render: (_: any, record: any) => (
        <Button type="dashed" icon={<PrinterOutlined />} size="small" onClick={() => handlePrintReceipt(record)}>
          In phiếu
        </Button>
      )
    },
    {
      title: 'In phiếu CK',
      key: 'printBank',
      width: 120,
      align: 'center' as const,
      render: (_: any, record: any) => (
        <Button 
          type="dashed" 
          icon={<PrinterOutlined />} 
          size="small" 
          disabled={!(changedRows[record.HocBuID]?.ChuyenKhoan ?? record.ChuyenKhoan)}
          onClick={() => handlePrintBankConfirmation(record)}
        >
          In xác nhận
        </Button>
      )
    },
  ];

  return (
    <Card title="Quản lý học bù, học lại - Kế toán" bordered style={{ margin: 16 }}>
      {/* Statistics */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={4}>
          <Card size="small">
            <Statistic
              title="Tổng bản ghi"
              value={currentStats.total}
              prefix={<DollarOutlined />}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card size="small">
            <Statistic
              title="Đã đóng"
              value={currentStats.paid}
              valueStyle={{ color: '#3f8600' }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card size="small">
            <Statistic
              title="Chưa đóng"
              value={currentStats.unpaid}
              valueStyle={{ color: '#cf1322' }}
              prefix={<ClockCircleOutlined />}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card size="small">
            <Statistic
              title="Tổng tiền"
              value={currentStats.totalAmount}
              suffix="đ"
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card size="small">
            <Statistic
              title="Tiền mặt"
              value={currentStats.paidCashAmount}
              suffix="đ"
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card size="small">
            <Statistic
              title="Chuyển khoản"
              value={currentStats.paidBankAmount}
              suffix="đ"
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card size="small">
            <Statistic
              title="Chưa thu"
              value={currentStats.unpaidAmount}
              suffix="đ"
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={8} style={{ marginBottom: 8 }}>
        <Col span={4}>
          <Select
            style={{ width: '100%' }}
            placeholder="Chọn khóa học"
            value={selectedCourse}
            onChange={setSelectedCourse}
            options={courses.map(c => ({ label: `${c.KhoaHocID} - ${c.TenKhoaHoc}`, value: c.KhoaHocID }))}
            allowClear
          />
        </Col>
        <Col span={4}>
          <Select
            style={{ width: '100%' }}
            placeholder="Chọn môn học"
            value={selectedSubject}
            onChange={setSelectedSubject}
            options={subjects.map(s => ({ label: `${s.MonHocID} - ${s.TenMonHoc}`, value: s.MonHocID }))}
            allowClear
            disabled={!selectedCourse}
          />
        </Col>
        <Col span={4}>
          <Select
            style={{ width: '100%' }}
            placeholder="Chọn lớp học phần"
            value={selectedClassSection}
            onChange={setSelectedClassSection}
            options={classSections.map(l => {
              const id = l.LopHocPhanID || l.id || l.lopHocPhanId || '';
              const name = l.TenLopHP || l.TenLopHocPhan || l.name || l.tenLopHP || id;
              return {
                label: `${id} - ${name}`,
                value: id
              };
            })}
            allowClear
            loading={loading}
            disabled={!selectedSubject}
          />
        </Col>
        <Col span={4}>
          <DatePicker
            style={{ width: '100%' }}
            placeholder="Lọc theo ngày học bù"
            value={selectedDate}
            onChange={date => setSelectedDate(date || dayjs())}
            format="DD/MM/YYYY"
            allowClear
          />
        </Col>
        <Col span={4}>
          <Input
            placeholder="Tìm kiếm sinh viên..."
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            prefix={<SearchOutlined />}
            allowClear
          />
        </Col>
        <Col span={4}>
          <Select
            style={{ width: '100%' }}
            placeholder="Lọc trạng thái"
            value={filterStatus}
            onChange={setFilterStatus}
            options={[
              { label: 'Tất cả', value: 'all' },
              { label: 'Chưa đóng tiền', value: 'unpaid' },
              { label: 'Đã đóng tiền', value: 'paid' }
            ]}
          />
        </Col>
      </Row>
      
      <Table
        loading={loading}
        columns={columns}
        dataSource={filteredData}
        rowKey="HocBuID"
        pagination={{ 
          pageSize: 20,
          showTotal: (total, range) => `${range[0]}-${range[1]} trên ${total} sinh viên`
        }}
        scroll={{ x: 900, y: 400 }}
        bordered
        size="middle"
        rowClassName={(record: any) => {
          const isPaid = record.DaDongTien || (changedRows[record.HocBuID]?.DaDongTien ?? false);
          return isPaid ? '' : 'unpaid-row';
        }}
      />
      
      <Space style={{ marginTop: 16 }}>
        <Button type="primary" icon={<SaveOutlined />} onClick={handleSave} disabled={Object.keys(changedRows).length === 0}>
          Lưu trạng thái đóng tiền
        </Button>
        <Button type="default" icon={<BarChartOutlined />} onClick={openStatisticsModal}>
          Thống kê bảng kê nộp tiền
        </Button>
        <Button type="default" icon={<PrinterOutlined />} onClick={handlePrintAllReceipts}>
          In tất cả phiếu thu (gộp)
        </Button>
      </Space>

      {/* Modal thống kê bảng kê nộp tiền */}
      <Modal
        title="Thống kê bảng kê nộp tiền học bù, học lại"
        open={statisticsModalVisible}
        onCancel={closeStatisticsModal}
        width={700}
        footer={[
          <Button key="cancel" onClick={closeStatisticsModal}>Đóng</Button>,
          <Button key="fetch" type="primary" onClick={fetchStatisticsData} loading={statisticsLoading}>Thống kê</Button>,
          <Button 
            key="print" 
            type="default" 
            icon={<PrinterOutlined />} 
            onClick={printStatisticsReport} 
            disabled={!statisticsData.length}
          >
            In bảng kê
          </Button>
        ]}
      >
        <Form layout="vertical">
          <Form.Item label="Loại thống kê">
            <Radio.Group value={statisticsType} onChange={handleStatisticsTypeChange}>
              <Radio.Button value="day">Theo ngày</Radio.Button>
              <Radio.Button value="month">Theo tháng</Radio.Button>
              <Radio.Button value="quarter">Theo quý</Radio.Button>
              <Radio.Button value="year">Theo năm</Radio.Button>
            </Radio.Group>
          </Form.Item>

          {statisticsType === 'day' && (
            <Form.Item label="Khoảng thời gian">
              <RangePicker 
                value={statisticsDateRange} 
                onChange={(dates) => {
                  if (dates && dates[0] && dates[1]) {
                    setStatisticsDateRange([dates[0], dates[1]]);
                  }
                }} 
                format="DD/MM/YYYY" 
                style={{ width: '100%' }}
              />
            </Form.Item>
          )}

          {statisticsType === 'month' && (
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="Tháng">
                  <Select
                    value={statisticsMonth}
                    onChange={setStatisticsMonth}
                    options={Array.from({ length: 12 }, (_, i) => ({ label: `Tháng ${i + 1}`, value: i }))}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Năm">
                  <Select
                    value={statisticsYear}
                    onChange={setStatisticsYear}
                    options={Array.from({ length: 10 }, (_, i) => ({ 
                      label: `${dayjs().year() - 5 + i}`, 
                      value: dayjs().year() - 5 + i 
                    }))}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
            </Row>
          )}

          {statisticsType === 'quarter' && (
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="Quý">
                  <Select
                    value={statisticsQuarter}
                    onChange={setStatisticsQuarter}
                    options={[1, 2, 3, 4].map(q => ({ label: `Quý ${q}`, value: q }))}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Năm">
                  <Select
                    value={statisticsYear}
                    onChange={setStatisticsYear}
                    options={Array.from({ length: 10 }, (_, i) => ({ 
                      label: `${dayjs().year() - 5 + i}`, 
                      value: dayjs().year() - 5 + i 
                    }))}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
            </Row>
          )}

          {statisticsType === 'year' && (
            <Form.Item label="Năm">
              <Select
                value={statisticsYear}
                onChange={setStatisticsYear}
                options={Array.from({ length: 10 }, (_, i) => ({ 
                  label: `${dayjs().year() - 5 + i}`, 
                  value: dayjs().year() - 5 + i 
                }))}
                style={{ width: '100%' }}
              />
            </Form.Item>
          )}

          {statisticsData.length > 0 && (
            <>
              <Divider>Kết quả thống kê</Divider>
              <div style={{ marginBottom: 16 }}>
                <strong>Tổng số bản ghi:</strong> {statisticsData.length}
                <br />
                <strong>Tổng số tiền:</strong> {statisticsData.reduce((sum, item) => sum + (item.ThanhTien || 0), 0).toLocaleString('vi-VN')} đồng
              </div>
              <Table 
                columns={[
                  { title: 'Mã SV', dataIndex: 'MaHS', key: 'MaHS' },
                  { title: 'Họ tên', dataIndex: 'HoTen', key: 'HoTen' },
                  { title: 'Lớp', dataIndex: 'TenLop', key: 'TenLop' },
                  { title: 'Môn học', dataIndex: 'TenMonHoc', key: 'TenMonHoc' },
                  { title: 'Hình thức', dataIndex: 'HinhThuc', key: 'HinhThuc' },
                  { title: 'PTTT', key: 'PTTT', render: (_: any, r: any) => (r.TienMat ? 'Tiền mặt' : (r.ChuyenKhoan ? 'Chuyển khoản' : '')) },
                  { 
                    title: 'Thành tiền', 
                    dataIndex: 'ThanhTien', 
                    key: 'ThanhTien',
                    render: (value: any) => (value || 0).toLocaleString('vi-VN'),
                    align: 'right' as const
                  }
                ]}
                dataSource={statisticsData}
                size="small"
                pagination={{ pageSize: 5 }}
                rowKey={(record: any, index: any) => `${record.HocBuID || index}`}
              />
            </>
          )}
        </Form>
      </Modal>

      <style>{`
        .unpaid-row {
          background-color: #fff7e6;
        }
        
        .unpaid-row:hover > td {
          background-color: #ffe7ba !important;
        }
      `}</style>
    </Card>
  );
};

export default AccountingMakeupManagementPage; 